<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

import SidePanelDrawer from 'dashboard/components-next/SidePanelDrawer.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const props = defineProps({
  contact: { type: Object, default: null },
});

const { t } = useI18n();
const store = useStore();

const drawerRef = ref(null);
const contactsFormRef = ref(null);
const isUpdating = ref(false);

const contactData = computed(() => ({
  id: props.contact?.id,
  name: props.contact?.name,
  email: props.contact?.email,
  phoneNumber: props.contact?.phone_number,
  companyId: props.contact?.company_id,
  additionalAttributes: props.contact?.additional_attributes,
  customAttributes: props.contact?.custom_attributes,
}));

const formData = ref({ ...contactData.value });

const handleFormUpdate = updated => {
  formData.value = { ...updated };
};

const open = () => drawerRef.value?.open();
const close = () => drawerRef.value?.close();

const handleUpdate = async () => {
  const data = contactsFormRef.value?.state || formData.value;
  isUpdating.value = true;
  try {
    await store.dispatch('contacts/update', data);
    await store.dispatch('contacts/fetchContactableInbox', props.contact.id);
    useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      const invalidAttrs = Array.isArray(error.data) ? error.data : [];
      if (invalidAttrs.includes('email')) {
        useAlert(t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
      } else if (invalidAttrs.includes('phone_number')) {
        useAlert(t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
      } else {
        useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
  } finally {
    isUpdating.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <SidePanelDrawer
    ref="drawerRef"
    :title="t('EDIT_CONTACT.TITLE')"
    :description="t('EDIT_CONTACT.DESC')"
  >
    <template v-if="contact">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        @update="handleFormUpdate"
      />

      <div class="flex items-center w-full gap-3 mt-6">
        <Button
          :label="t('EDIT_CONTACT.ACTIONS.UPDATE')"
          color="blue"
          :is-loading="isUpdating"
          @click="handleUpdate"
        />
        <Button
          :label="t('EDIT_CONTACT.ACTIONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="close"
        />
      </div>
    </template>
  </SidePanelDrawer>
</template>
